import { useMemo, useState, useEffect } from "react"
import _ from "lodash";
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { Box, Grid2, Stack, Typography, useTheme } from "@mui/material"
import { EGAccordionSingle } from "../../../../MainComponents/Accordion/EGAccordion/EGAccordion"
import { DetailsTodo, InvestigationBlockInfo, InvestigationButton, InvestigationCollapsable, Separator, UploadedFile } from "../components"
import Icon from "../../../../Icon/Icon"
import { useConstants } from "../Main/useConstants"
import { AddFiles } from "./Modals/AddFiles";
import { catalogByPart } from "../../../../../apis";

export const List = ({
  plans,
  setPlans,
  handleSavePlans,
  setIsEditing,
  setSelectedActivity,
  reloadInvestigation,
  justView,
}) => {

  const theme = useTheme();
  const { enums } = useConstants();
  const [t] = useTranslation("investigation");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showAddFiles, setShowAddFiles] = useState(false);
  const [activityPlanOptions, setActivityPlanOptions] = useState([]);

  const [pending, process, completed] = useMemo(() => {
    const pendingTemp = [];
    const processTemp = [];
    const completedTemp = [];
    plans.forEach((plan, trueIdx) => {
      const status = plan.status;
      if (status === enums.statusPlan.PENDING) {
        pendingTemp.push({ ...plan, trueIdx });
      } else if (status === enums.statusPlan.PROCESS) {
        processTemp.push({ ...plan, trueIdx });
      } else if (status === enums.statusPlan.COMPLETED) {
        completedTemp.push({ ...plan, trueIdx });
      }
    })
    return [pendingTemp, processTemp, completedTemp];
  }, [plans])
  
  const titles = {
    [enums.statusPlan.PENDING]: {
      title: t("activityPlan.pending"),
      color: theme.palette.primary.main,
    },
    [enums.statusPlan.PROCESS]: {
      title: t("activityPlan.in_process"),
      color: theme.palette.purple.main,
    },
    [enums.statusPlan.COMPLETED]: {
      title: t("activityPlan.completed"),
      color: theme.palette.green.main,
    },
  };

  const handleShowAddFiles = (idx) => {
    setSelectedPlan(idx);
    setShowAddFiles(true);
  }

  useEffect(() => {
    const loadActivityPlanOptions = async () => {
      try {
        const response = await catalogByPart({
          catalogue: "activityPlan",
          is_own: true,
          path: "/"
        });
        if (response?.data) {
          const options = response.data.map((item) => ({
            label: item.label || item.value,
            value: item.path_locales || item.value,
          }));
          setActivityPlanOptions(options);
        }
      } catch (error) {
        console.error('Error loading activityPlan catalogue:', error);
      }
    };

    loadActivityPlanOptions();
  }, []);

  const getActivityNameLabel = (nameValue) => {
    if (!nameValue) return "";
    const found = activityPlanOptions.find(opt => opt.value === nameValue);
    return found ? found.label : nameValue;
  };

  return (
    <>
      {
        !justView && (
          <AddFiles
            show={showAddFiles}
            onHide={() => setShowAddFiles(false)}
            plans={plans}
            selectedPlan={selectedPlan}
            setSelectedPlan={setSelectedPlan}
            handleSavePlans={handleSavePlans}
            reloadInvestigation={reloadInvestigation}
          />
        )
      }
      <Stack spacing={2} p={ justView ? 0 : 2 }>
        {
          pending.length > 0 && pending.map((plan) => (
            <SinglePlan
              key={plan.id}
              preTitle={titles[enums.statusPlan.PENDING].title}
              plan={plan}
              plans={plans}
              color={titles[enums.statusPlan.PENDING].color}
              handleSavePlans={handleSavePlans}
              setPlans={setPlans}
              setIsEditing={setIsEditing}
              setSelectedActivity={setSelectedActivity}
              handleShowAddFiles={handleShowAddFiles}
              justView={justView}
              getActivityNameLabel={getActivityNameLabel}
            />
          ))
        }
        {
          process.length > 0 && process.map((plan) => (
            <SinglePlan
              key={plan.id}
              preTitle={titles[enums.statusPlan.PROCESS].title}
              plan={plan}
              plans={plans}
              color={titles[enums.statusPlan.PROCESS].color}
              handleSavePlans={handleSavePlans}
              setPlans={setPlans}
              setIsEditing={setIsEditing}
              setSelectedActivity={setSelectedActivity}
              handleShowAddFiles={handleShowAddFiles}
              justView={justView}
              getActivityNameLabel={getActivityNameLabel}
            />
          ))
        }
        {
          completed.length > 0 && completed.map((plan) => (
            <SinglePlan
              key={plan.id}
              preTitle={titles[enums.statusPlan.COMPLETED].title}
              plan={plan}
              plans={plans}
              color={titles[enums.statusPlan.COMPLETED].color}
              handleSavePlans={handleSavePlans}
              setPlans={setPlans}
              setIsEditing={setIsEditing}
              setSelectedActivity={setSelectedActivity}
              handleShowAddFiles={handleShowAddFiles}
              justView={justView}
              getActivityNameLabel={getActivityNameLabel}
            />
          ))
        }
      </Stack>
    </>
  )

}

const SinglePlan = ({
  plan,
  plans,
  preTitle,
  color,
  handleSavePlans,
  setPlans,
  setIsEditing,
  setSelectedActivity,
  handleShowAddFiles,
  justView,
  getActivityNameLabel,
}) => {

  const { functions: { getResourceLabel, getUserLabel } } = useConstants();

  const [t] = useTranslation("investigation");
  const theme = useTheme();

  const filesByDescription = useMemo(() => {
    if (!plan.files || plan.files.length === 0) return [];
    const groupedFiles = [];
    plan.files.forEach(file => {
      const description = file.description || "";
      const group = groupedFiles.findIndex(g => g.description === description);
      if (group === -1) {
        groupedFiles.push({ description, files: [file] });
      } else {
        groupedFiles[group].files.push(file);
      }
    });
    return groupedFiles;
  }, [plan.files]);

  const deadline = useMemo(() => {
    const fecha = dayjs(plan.deadline).format("DD/MMM/YYYY");
    if (fecha == "Invalid Date") return t("activityPlan.not_scheduled");
    const hora = plan.timeDeadline ? `- ${plan.timeDeadline}` : "";
    return `${fecha.toLocaleUpperCase()} ${hora}`;
  }, [plan.deadline, plan.timeDeadline]);

  const handleEditPlan = async() => {
    setSelectedActivity(plan.trueIdx);
    setIsEditing(true);
  }

  const handleRemovePlan = async () => {
    const newPlans = plans.filter((_, idx) => idx !== plan.trueIdx);
    handleSavePlans && await handleSavePlans(newPlans);
    setPlans(newPlans);
    setSelectedActivity(null);
  }

  const actions = [
    {
      name: t("activityPlan.edit"),
      icon: <Icon name="outline_edit" color={theme.palette.primary.main} />,
      onClick: handleEditPlan,
    },
    {
      name: t("activityPlan.delete"),
      icon: <Icon name="trash_can" color={theme.palette.error.main} />,
      onClick: handleRemovePlan,
    },
  ];

  return (
    <EGAccordionSingle title={`${preTitle}: ${getActivityNameLabel(plan.name)}`} bgColor={color} color={theme.palette.white.main} actions={justView ? [] : actions} >
      <Grid2 container spacing={2}>
        
        <Grid2 size={{ xs: 12, md: 4 }}>
          <InvestigationBlockInfo
            label={t("activityPlan.responsible_of_the_activity")}
            text={getUserLabel(plan.assignedTo) ?? t("Sin asignar")}
            size={12}
          />
        </Grid2>
        
        <Grid2 size={{ xs: 12, md: 4 }}>
          <InvestigationBlockInfo
            label={t("activityPlan.participants")}
            text={plan?.party?.map(item => getUserLabel(item)).join(", ") ?? t("Sin asignar")}
            size={12}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 4 }}>
          <InvestigationBlockInfo
            label={t("activityPlan.date")}
            text={deadline}
            size={12}
          />
        </Grid2>

        <Grid2 size={12}>
          <InvestigationBlockInfo
            label={t("activityPlan.details")}
            text={plan.details}
            size={12}
          />
        </Grid2>

      </Grid2>

      {
        plan.resources?.length > 0 &&
        <>
          <Separator text={t("activityPlan.assigned_resources")} sx={{ my: 2 }} />
          <Stack spacing={2}>
          {
            plan.resources.map((resource, idx) => (
              <InvestigationCollapsable 
                title={
                  <Typography color="secondary.main" fontSize={14} fontWeight={600}>
                    {t("activityPlan.access_resource")}:&nbsp;
                    <Typography component="span" fontSize={16} fontWeight={700}>
                      {getResourceLabel(resource)}
                    </Typography>
                  </Typography>
                }
                details={ resource.details &&
                  <Typography color="blueGray600.main" fontSize={11} fontWeight={600}>
                    {t("activityPlan.answer")}:&nbsp;
                    <Typography color="oxfordBlue.main" component="span" fontSize={11} fontStyle="italic">
                      {resource.details}
                    </Typography>
                  </Typography>
                }
              />
            ))
          }
          </Stack>
        </>
      }

      {
        plan.todos?.length > 0 &&
        <>
          <Separator text={t("activityPlan.assigned_tasks")} sx={{ my: 2 }} />
          <Stack spacing={2}>
          {
            plan.todos.map((todo, idx) => (
              <InvestigationCollapsable
                key={idx}
                title={
                  <Typography color="secondary.main" fontSize={14}>
                    {t("activityPlan.task")} {todo.completed ? t("activityPlan.completed") : t("activityPlan.pending")}:&nbsp;
                    <Typography component="span" fontSize={16} fontWeight={700}>
                      {typeof todo === "string" ? todo : todo.name}
                    </Typography>
                  </Typography>
                }
                details={ <DetailsTodo todo={todo} /> }
              />
            ))
          }
          </Stack>
        </>
      }

      {
        filesByDescription.length > 0 &&
        <>
          <Separator text={t("activityPlan.support_files_for_the_activity")} sx={{ my: 2 }} />
          <Grid2 container spacing={2}>
          {
            filesByDescription.map((fileGroup, idx) => (
              <>
                {
                  fileGroup.description &&
                  <Grid2 size={{ xs: 12 }} key={`${idx}-title`}>
                    <InvestigationBlockInfo
                      label={t("activityPlan.description")}
                      text={fileGroup.description}
                      richTextEditor 
                      size={12}
                    />
                  </Grid2>
                }
                {
                  fileGroup.files.map((file, fileIdx) => (
                    <Grid2 size={{ xs: 12, lg: 6 }} key={`${fileIdx}-file`}>
                      <UploadedFile
                        key={fileIdx}
                        text={file.name}
                        downloadUrl={file.file_url}
                      />
                    </Grid2>
                  ))
                }
              </>
            ))
          }
          </Grid2>
        </>
      }

      {
        !justView && (
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }}>
            <InvestigationButton
              label={t("activityPlan.upload_files")}
              onClick={() => handleShowAddFiles(plan.trueIdx)}
            />
          </Box>
        ) 
      }


    </EGAccordionSingle>
  )
}
